import type { CreateSiteData, PipelineInfo, Site } from '@/types/site';
import type { SiteAddTabProps, SiteEditTabProps } from './types';

type PipelineInfoTabProps = SiteEditTabProps | SiteAddTabProps;

export default function PipelineInfoTab({
  site,
  onSiteChange: handleSiteChange,
}: PipelineInfoTabProps) {
  // 타입 안전한 사이트 변경 핸들러
  const handleSiteChangeTyped = (updatedSite: Site | CreateSiteData) => {
    // 타입 단언: SiteEditTabProps와 SiteAddTabProps의 유니온 타입 처리
    (handleSiteChange as (site: Site | CreateSiteData) => void)(updatedSite);
  };
  // 급수 배관 정보 업데이트
  const updateWaterSupplyPipeline = (
    index: number,
    updates: Partial<PipelineInfo>
  ) => {
    const pipelines = site.waterSupplyPipelineInfo || [];
    const newPipelines = [...pipelines];
    newPipelines[index] = {
      ...newPipelines[index],
      ...updates,
    };
    handleSiteChangeTyped({
      ...site,
      waterSupplyPipelineInfo: newPipelines,
    });
  };

  // 배수 배관 정보 업데이트
  const updateDrainagePipeline = (
    index: number,
    updates: Partial<PipelineInfo>
  ) => {
    const pipelines = site.drainageSupplyPipelineInfo || [];
    const newPipelines = [...pipelines];
    newPipelines[index] = {
      ...newPipelines[index],
      ...updates,
    };
    handleSiteChangeTyped({
      ...site,
      drainageSupplyPipelineInfo: newPipelines,
    });
  };

  // 급수 배관 추가/제거
  const addWaterSupplyPipeline = () => {
    const newPipeline: PipelineInfo = {
      type: '',
      diameter: 0,
      extension: 0,
    };
    handleSiteChangeTyped({
      ...site,
      waterSupplyPipelineInfo: [
        ...(site.waterSupplyPipelineInfo || []),
        newPipeline,
      ],
    });
  };

  const removeWaterSupplyPipeline = (index: number) => {
    const pipelines = site.waterSupplyPipelineInfo || [];
    handleSiteChangeTyped({
      ...site,
      waterSupplyPipelineInfo: pipelines.filter((_, i) => i !== index),
    });
  };

  // 배수 배관 추가/제거
  const addDrainagePipeline = () => {
    const newPipeline: PipelineInfo = {
      type: '',
      diameter: 0,
      extension: 0,
    };
    handleSiteChangeTyped({
      ...site,
      drainageSupplyPipelineInfo: [
        ...(site.drainageSupplyPipelineInfo || []),
        newPipeline,
      ],
    });
  };

  const removeDrainagePipeline = (index: number) => {
    const pipelines = site.drainageSupplyPipelineInfo || [];
    handleSiteChangeTyped({
      ...site,
      drainageSupplyPipelineInfo: pipelines.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-8">
      {/* 급수 배관 정보 */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">급수 배관 정보</h3>
          <button
            onClick={addWaterSupplyPipeline}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
          >
            배관 추가
          </button>
        </div>
        {site.waterSupplyPipelineInfo?.map((pipeline, index) => (
          <div key={index} className="bg-white p-4 rounded-lg mb-4 border">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium">급수 배관 {index + 1}</h4>
              <button
                onClick={() => removeWaterSupplyPipeline(index)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                삭제
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  배관 유형
                </label>
                <select
                  value={pipeline.type || ''}
                  onChange={(e) =>
                    updateWaterSupplyPipeline(index, { type: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">선택하세요</option>
                  <option value="PVC">PVC</option>
                  <option value="PE">PE</option>
                  <option value="강관">강관</option>
                  <option value="주철관">주철관</option>
                  <option value="스테인리스">스테인리스</option>
                  <option value="기타">기타</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  배관 직경 (mm)
                </label>
                <input
                  type="number"
                  value={pipeline.diameter || ''}
                  onChange={(e) =>
                    updateWaterSupplyPipeline(index, {
                      diameter: Number(e.target.value) || 0,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  배관 길이 (m)
                </label>
                <input
                  type="number"
                  value={pipeline.extension || ''}
                  onChange={(e) =>
                    updateWaterSupplyPipeline(index, {
                      extension: Number(e.target.value) || 0,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )) || (
          <p className="text-gray-500 text-center py-4">
            등록된 급수 배관이 없습니다.
          </p>
        )}
      </div>

      {/* 배수 배관 정보 */}
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">배수 배관 정보</h3>
          <button
            onClick={addDrainagePipeline}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
          >
            배관 추가
          </button>
        </div>
        {site.drainageSupplyPipelineInfo?.map((pipeline, index) => (
          <div key={index} className="bg-white p-4 rounded-lg mb-4 border">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium">배수 배관 {index + 1}</h4>
              <button
                onClick={() => removeDrainagePipeline(index)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                삭제
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  배관 유형
                </label>
                <select
                  value={pipeline.type || ''}
                  onChange={(e) =>
                    updateDrainagePipeline(index, { type: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">선택하세요</option>
                  <option value="PVC">PVC</option>
                  <option value="PE">PE</option>
                  <option value="강관">강관</option>
                  <option value="주철관">주철관</option>
                  <option value="스테인리스">스테인리스</option>
                  <option value="기타">기타</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  배관 직경 (mm)
                </label>
                <input
                  type="number"
                  value={pipeline.diameter || ''}
                  onChange={(e) =>
                    updateDrainagePipeline(index, {
                      diameter: Number(e.target.value) || 0,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  배관 길이 (m)
                </label>
                <input
                  type="number"
                  value={pipeline.extension || ''}
                  onChange={(e) =>
                    updateDrainagePipeline(index, {
                      extension: Number(e.target.value) || 0,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )) || (
          <p className="text-gray-500 text-center py-4">
            등록된 배수 배관이 없습니다.
          </p>
        )}
      </div>
    </div>
  );
}
